import { Button, Input, Select } from '@fruuit/ui'
import { Search, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { STAGE_OPTIONS, STATUS_OPTIONS, THEME_OPTIONS } from './format'
import type { EngagementListParams } from './queries'

const FILTER_KEYS = ['q', 'status', 'theme', 'stage', 'issuer_id'] as const

/** Engagement list filters live in the URL, so links like `?status=stalled` work. */
export function useEngagementFilters() {
  const [params, setParams] = useSearchParams()
  const values: EngagementListParams = {}
  for (const key of FILTER_KEYS) {
    const value = params.get(key)
    if (value) values[key] = value
  }

  const update = useCallback(
    (next: EngagementListParams) => {
      const out = new URLSearchParams()
      for (const key of FILTER_KEYS) {
        const value = next[key]
        if (value) out.set(key, value)
      }
      setParams(out, { replace: true })
    },
    [setParams],
  )

  return { values, update }
}

export function EngagementFilters({
  values,
  onChange,
}: {
  values: EngagementListParams
  onChange: (next: EngagementListParams) => void
}) {
  const [text, setText] = useState(values.q ?? '')

  useEffect(() => setText(values.q ?? ''), [values.q])

  useEffect(() => {
    if (text === (values.q ?? '')) return
    const timer = setTimeout(() => onChange({ ...values, q: text.trim() || undefined }), 300)
    return () => clearTimeout(timer)
  }, [text, values, onChange])

  const active = Object.values(values).some(Boolean)
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(14rem,1.6fr)_repeat(3,minmax(9rem,1fr))_auto] lg:items-end">
      <div className="relative">
        <Input
          label="Search"
          hideLabel
          type="search"
          placeholder="Search company or case"
          value={text}
          onChange={(event) => setText(event.target.value)}
          inputClassName="pl-9"
        />
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-sage"
        />
      </div>
      <Select
        label="Status"
        hideLabel
        placeholder="All statuses"
        options={STATUS_OPTIONS}
        value={values.status ?? ''}
        onChange={(event) => onChange({ ...values, status: event.target.value || undefined })}
      />
      <Select
        label="Theme"
        hideLabel
        placeholder="All themes"
        options={THEME_OPTIONS}
        value={values.theme ?? ''}
        onChange={(event) => onChange({ ...values, theme: event.target.value || undefined })}
      />
      <Select
        label="Stage"
        hideLabel
        placeholder="Any stage"
        options={STAGE_OPTIONS}
        value={values.stage ?? ''}
        onChange={(event) => onChange({ ...values, stage: event.target.value || undefined })}
      />
      <Button
        variant="ghost"
        icon={<X />}
        disabled={!active}
        onClick={() => {
          setText('')
          onChange({})
        }}
      >
        Reset
      </Button>
    </div>
  )
}
